import { parseFilter, parseCourseType } from '@/util';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PersonAvatar, TEOLoader } from '@teo/components';
import { useNavigate } from 'react-router-dom';
import { useGet } from '@/query/fetchHooks';

export const SearchResult = ({
  authUser,
  filter,
  searchParams,
  setActiveSearch,
  organisationRole,
  searchText,
}: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const hasHigherRole = organisationRole > 20;
  const params = { ...searchParams, count: 12, q: parseFilter(filter) };
  const learnerTypes = (searchParams.type || 'course,OR,type=learningpath')
    .split(',OR,type=')
    .filter((type: string) => type === 'course' || type === 'learningpath')
    .join(',OR,type=');
  const learnerResult = useGet<any[]>('/mystudentglobalindex', {
    ...params,
    type: learnerTypes,
    enabled: !!learnerTypes,
    subtype: 'course,OR,subtype=learningpath',
  });
  // Keep learner rows out of the general query, including its result limit.
  const otherTypes = (
    searchParams.type ||
    'coursecontent,OR,type=learningpathcontent,OR,type=member,OR,type=team'
  )
    .split(',OR,type=')
    .filter((type: string) => type !== 'course' && type !== 'learningpath')
    .join(',OR,type=');
  const otherResult = useGet<any[]>('/myglobalindex', {
    ...params,
    type: otherTypes,
    enabled: hasHigherRole && !!otherTypes,
  });
  const loadeResult =
    searchText !== filter ||
    (!!learnerTypes && learnerResult.isFetching) ||
    (hasHigherRole && !!otherTypes && otherResult.isFetching);
  const data = useMemo(
    () => [
      ...(learnerTypes ? learnerResult.data?.data || [] : []),
      ...(hasHigherRole && otherTypes ? otherResult.data?.data || [] : []),
    ],
    [
      learnerResult.data,
      otherResult.data,
      hasHigherRole,
      learnerTypes,
      otherTypes,
    ]
  );
  const isEmpty = data.length === 0;
  const users = data.filter((el) => el.type === 'member');
  const teams = data.filter((el) => el.type === 'team');
  const courses = data.filter(
    (el) => el.type === 'course' || el.type === 'coursecontent'
  );
  const searchCourses = courses.filter((el) => el.courseType === 'course');
  const searchAssessments = courses.filter(
    (el) => el.courseType === 'assessment'
  );
  const searchVacancies = courses.filter(
    (el) => el.courseType === 'leervacature'
  );
  const searchLearningPaths = data.filter(
    (el) => el.type === 'learningpath' || el.type === 'learningpathcontent'
  );

  const escapeRegExp = (str: string) =>
    str?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const highlightText = (text: string, query: string) => {
    if (!query) return text;

    const safeQuery = escapeRegExp(query);
    const regex = new RegExp(`(${safeQuery})`, 'gi');

    return text?.split(regex)?.map((part, index) => {
      if (part?.toLowerCase() === query?.toLowerCase()) {
        return (
          <span key={index} className="bg-[#FEEDBB] ">
            {part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  const goToPage = (item: any, ev: any) => {
    ev?.stopPropagation();
    if (item?.subtype === 'member') {
      navigate(
        `/${authUser?.lang}/follow-up/${authUser?.activeOrganisationId}/users/${item?.objectId}`
      );
      setActiveSearch(false);
      document.body.style.overflow = 'unset';
    }
    if (item?.subtype === 'team') {
      navigate(
        `/${authUser?.lang}/follow-up/${authUser?.activeOrganisationId}/teams/${item?.objectId}`
      );
      setActiveSearch(false);
      document.body.style.overflow = 'unset';
    }
    if (item?.type === 'course') {
      navigate(
        `/${authUser?.lang}/learn/${
          authUser?.activeOrganisationId
        }/${parseCourseType(item?.courseType)}/${item?.objectId}`
      );
      setActiveSearch(false);
      document.body.style.overflow = 'unset';
    }
    if (
      item.type === 'coursecontent' &&
      item.subtype === 'coursecontent_create'
    ) {
      navigate(
        `/${authUser?.lang}/create/${
          authUser?.activeOrganisationId
        }/${parseCourseType(item?.courseType)}/${item?.objectId}`
      );
      setActiveSearch(false);
      document.body.style.overflow = 'unset';
    }
    if (
      item.type === 'coursecontent' &&
      item.subtype === 'coursecontent_follow'
    ) {
      navigate(
        `/${authUser?.lang}/follow-up/${
          authUser?.activeOrganisationId
        }/${parseCourseType(item?.courseType)}/${item?.objectId}`
      );
      setActiveSearch(false);
      document.body.style.overflow = 'unset';
    }
    if (item?.type === 'learningpath') {
      navigate(
        `/${authUser?.lang}/learn/${authUser?.activeOrganisationId}/learning-paths/${item?.objectId}`
      );
      setActiveSearch(false);
      document.body.style.overflow = 'unset';
    }
    if (
      item?.type === 'learningpathcontent' &&
      item.subtype === 'learningpath_follow'
    ) {
      navigate(
        `/${authUser?.lang}/follow-up/${authUser?.activeOrganisationId}/learning-paths/${item?.objectId}`
      );
      setActiveSearch(false);
      document.body.style.overflow = 'unset';
    }
    if (
      item?.type === 'learningpathcontent' &&
      item.subtype === 'learningpath_create'
    ) {
      navigate(
        `/${authUser?.lang}/create/${authUser?.activeOrganisationId}/learning-paths/${item?.objectId}`
      );
      setActiveSearch(false);
      document.body.style.overflow = 'unset';
    }
  };

  return (
    <div className="relative flex min-h-24 flex-col">
      {loadeResult && (
        <div className="absolute inset-0 z-10 flex bg-white/25 p-2 backdrop-blur-sm">
          <div className="m-auto">
            <TEOLoader
              loaderSrc="/images/TEOloader.gif"
              className="w-[110px]"
            />
          </div>
        </div>
      )}
      {isEmpty ? (
        <>
          {!loadeResult && (
            <div className="my-8 flex flex-col p-4 text-center">
              <img
                className="m-auto w-48"
                src="/images/components/notifications/theo-empty-inbox.svg"
              />
              <p className="pt-4 text-sm text-grey-06">
                {t('global_search.empty_result')}
              </p>
            </div>
          )}
        </>
      ) : (
        <>
          {users?.length > 0 && (
            <div className="flex flex-col gap-1 border-t border-grey-02 p-4">
              <p className="text-sm text-[#231F20]">{t('breadcrumbs.USERS')}</p>

              {users?.map((el: any, i: any) => {
                return (
                  <div
                    className="flex cursor-pointer items-center rounded-lg py-1 px-2 hover:bg-grey-01"
                    key={el?.id + '_' + el?.subtype + '_' + i}
                    onClick={(ev) => goToPage(el, ev)}
                  >
                    <PersonAvatar
                      inline
                      id={el.id}
                      name={el.name}
                      imageSrc={el.picture}
                    ></PersonAvatar>

                    <div>
                      <p className="text-sm text-[#231F20]">
                        {highlightText(el.name, searchText)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {teams?.length > 0 && (
            <div className="flex flex-col gap-1 border-t border-grey-02 p-4">
              <p className="text-sm text-[#231F20]">{t('breadcrumbs.TEAMS')}</p>

              {teams?.map((el: any, i: any) => {
                return (
                  <div
                    className="flex cursor-pointer items-center rounded-lg py-1 px-2 hover:bg-grey-01"
                    key={el?.id + '_' + el?.subtype + '_' + i}
                    onClick={(ev) => goToPage(el, ev)}
                  >
                    <PersonAvatar
                      inline
                      id={el?.id}
                      name={el?.name}
                      bgColor={el?.color ? el?.color : null}
                    ></PersonAvatar>

                    <p className="text-sm text-[#231F20]">
                      {highlightText(el.name, searchText)}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
          {searchCourses?.length > 0 && (
            <div className="flex flex-col gap-1 border-t border-grey-02 p-4">
              <p className="text-sm text-[#231F20]">
                {t('breadcrumbs.COURSES')}
              </p>
              {searchCourses?.map((el: any, i: any) => {
                return (
                  <div
                    key={el?.id + '_' + el?.subtype + '_' + i}
                    onClick={(ev) => goToPage(el, ev)}
                    className="flex cursor-pointer items-center rounded-lg py-1 px-2 hover:bg-grey-01"
                  >
                    <div className="mr-3 w-16 min-w-16">
                      <img
                        src={el.picture || '/images/courseFallback.png'}
                        alt={el.name}
                        className="inline h-12 w-full rounded-lg object-cover"
                      />
                    </div>
                    <p className="text-sm text-[#231F20]">
                      {highlightText(el.name, searchText)}
                      {/* - {el.objectId} - {el.subtype} - {el.type} */}
                    </p>
                    {organisationRole > 20 && (
                      <p className="ml-auto text-sm text-grey-05">
                        {el?.subtype === 'coursecontent_follow'
                          ? t('global_search.following_course')
                          : el?.subtype === 'coursecontent_create'
                          ? t('global_search.creator_course')
                          : t('global_search.learner_course')}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {searchAssessments?.length > 0 && (
            <div className="flex flex-col gap-1 border-t border-grey-02 p-4">
              <p className="text-sm text-[#231F20]">
                {t('breadcrumbs.ASSESSMENTS')}
              </p>

              {searchAssessments?.map((el: any, i: any) => {
                return (
                  <div
                    key={el?.id + '_' + el?.subtype + '_' + i}
                    onClick={(ev) => goToPage(el, ev)}
                    className="flex cursor-pointer items-center rounded-lg py-1 px-2 hover:bg-grey-01"
                  >
                    <div className="mr-3 w-16 min-w-16">
                      <img
                        src={el.picture || '/images/courseFallback.png'}
                        alt={el.name}
                        className="inline h-12 w-full rounded-lg object-cover"
                      />
                    </div>
                    <p className="text-sm text-[#231F20]">
                      {highlightText(el.name, searchText)}
                    </p>
                    {organisationRole > 20 && (
                      <p className="ml-auto text-sm text-grey-05">
                        {el?.subtype === 'coursecontent_follow'
                          ? t('global_search.following_assessment')
                          : el?.subtype === 'coursecontent_create'
                          ? t('global_search.creator_assessment')
                          : t('global_search.learner_assessment')}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {searchVacancies?.length > 0 && (
            <div className="flex flex-col gap-1 border-t border-grey-02 p-4">
              <p className="text-sm text-[#231F20]">
                {t('breadcrumbs.VACANCIES')}
              </p>

              {searchVacancies?.map((el: any, i: any) => {
                return (
                  <div
                    key={el?.id + '_' + el?.subtype + '_' + i}
                    onClick={(ev) => goToPage(el, ev)}
                    className="flex cursor-pointer items-center rounded-lg py-1 px-2 hover:bg-grey-01"
                  >
                    <div className="mr-3 w-16 min-w-16">
                      <img
                        src={el.picture || '/images/courseFallback.png'}
                        alt={el.name}
                        className="inline h-12 w-full rounded-lg object-cover"
                      />
                    </div>
                    <p className="text-sm text-[#231F20]">
                      {highlightText(el.name, searchText)}
                    </p>
                    {organisationRole > 20 && (
                      <p className="ml-auto text-sm text-grey-05">
                        {el?.subtype === 'coursecontent_follow'
                          ? t('global_search.following_vacancy')
                          : el?.subtype === 'coursecontent_create'
                          ? t('global_search.creator_vacancy')
                          : t('global_search.learner_vacancy')}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {searchLearningPaths?.length > 0 && (
            <div className="flex flex-col gap-1 border-t border-grey-02 p-4">
              <p className="text-sm text-[#231F20]">
                {t('breadcrumbs.LEARNINGPATH')}
              </p>

              {searchLearningPaths?.map((el: any, i: any) => {
                return (
                  <div
                    key={el?.id + '_' + el?.subtype + '_' + i}
                    onClick={(ev) => goToPage(el, ev)}
                    className="flex cursor-pointer items-center rounded-lg py-1 px-2 hover:bg-grey-01"
                  >
                    <div className="mr-3 w-16 min-w-16">
                      <img
                        src={el.picture || '/images/courseFallback.png'}
                        alt={el.name}
                        className="inline h-12 w-full rounded-lg object-cover"
                      />
                    </div>
                    <p className="text-sm text-[#231F20]">
                      {highlightText(el.name, searchText)}
                    </p>
                    {organisationRole > 20 && (
                      <p className="ml-auto text-sm text-grey-05">
                        {el?.subtype === 'learningpath_follow'
                          ? t('global_search.following_learning_path')
                          : el?.subtype === 'learningpath_create'
                          ? t('global_search.creator_learning_path')
                          : t('global_search.learner_learning_path')}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};
